/* eslint-disable fp/no-mutation */
import { ReactElement, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import LiveTvIcon from '@mui/icons-material/LiveTv'
import Grid from '@mui/material/Grid'
import Timeline from '@mui/lab/Timeline'
import TimelineItem from '@mui/lab/TimelineItem'
import TimelineSeparator from '@mui/lab/TimelineSeparator'
import TimelineConnector from '@mui/lab/TimelineConnector'
import TimelineContent from '@mui/lab/TimelineContent'
import TimelineDot from '@mui/lab/TimelineDot'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StaticDateTimePicker } from '@mui/x-date-pickers/StaticDateTimePicker'

import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import theme from 'src/theme'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { TimelineOppositeContent } from '@mui/lab'
import { useNavigate } from 'react-router-dom'
import {
  CancelIcon,
  DeleteIcon,
  EditIcon,
  ImageIcon,
  SearchIcon,
  SubmitIcon,
  UploadFileIcon,
} from 'src/v3/core/icons'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import {
  fetchLiveClassSessions,
  fetchLiveClasses,
  fetchLiveClassCourses,
  fetchLiveClassTemplates,
  selectLiveClassById,
  selectLiveClassSessions,
  selectLiveClassSessionsByLiveClassId,
  selectLiveClassSessionsStatus,
  selectLiveClasses,
  selectLiveClassesGroupedByID,
  selectLiveClassesStatus,
  selectLiveClassTemplatesStatus,
  selectLiveClassTemplates,
} from '../admin.slice'
import Subheader from '../../components/subheader'

dayjs.extend(utc)

let updateLiveClassImage = (data: { id: string; imageType: string }) =>
  axios.put<string>('/api/v2/admin/live-class/image', data).then(pluckData)

export default (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()

  let [searchedLiveClassID, setSearchedLiveClassID] = useState<string>('')

  let liveClassesStatus = useAppSelector(selectLiveClassesStatus)
  let liveClasses = useAppSelector(selectLiveClasses)
  let selectedLiveClass = useAppSelector(
    selectLiveClassById(searchedLiveClassID),
  )
  let liveClassSessionsStatus = useAppSelector(selectLiveClassSessionsStatus)
  let liveClassSessions = useAppSelector(
    selectLiveClassSessionsByLiveClassId(searchedLiveClassID),
  )
  let allSessions = useAppSelector(selectLiveClassSessions)
  let liveClassesGroupedByID = useAppSelector(selectLiveClassesGroupedByID)
  let templates = useAppSelector(selectLiveClassTemplates)
  let templatesStatus = useAppSelector(selectLiveClassTemplatesStatus)

  let [searchOpen, setSearchOpen] = useState(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      code: yup.string().required(),
      meetingURL: yup.string().required(),
      quizPasscode: yup.string().required(),
      hours: yup.number().not([0]).required(),
      templateID: yup.string().not(['unselected']).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      code: '',
      meetingURL: '',
      quizPasscode: '',
      hours: 0,
      templateID: 'unselected',
    },
    onSubmit: (values) => {
      setSaving(true)
      axios
        .post('/api/v2/admin/live-class/course', {
          code: values.code,
          courseName: values.name,
          courseLength: values.hours,
          templateID: values.templateID,
        })
        .then(() =>
          axios.post('/api/v2/admin/live-class', {
            name: values.name,
            meetingUrl: values.meetingURL,
            quizPasscode: values.quizPasscode,
          }),
        )
        .then(pluckData)
        .then((data) =>
          axios
            .put('/api/v2/admin/live-class', {
              ...data,
              courseCode: values.code,
            })
            .then(() => setSearchedLiveClassID(data.id))
            .then(() =>
              updateLiveClassImage({
                id: data.id,
                imageType: fileToUpload?.extension === 'jpeg' ? 'jpg' : 'png',
              }),
            )
            .then((response) =>
              axios.put(response, fileToUpload?.file, {
                headers: {
                  'Cache-Control': 'max-age=2592000',
                  'content-type': fileToUpload?.type,
                },
              }),
            ),
        )
        .then(() => dispatch(fetchLiveClassCourses()))
        .then(() => dispatch(fetchLiveClasses()))
        .then(() => setFormOpen(false))
        .then(() => setSaving(false))
        .then(() => form.resetForm())
        .catch((err) => {
          setErrorMessage(
            err?.response?.data || err?.message || 'Error Creating Live Class',
          )
          dispatch(fetchLiveClassCourses())
          dispatch(fetchLiveClasses())
          setFormOpen(false)
          setSaving(false)
        })
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Live Class Image Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  useEffect(() => {
    if (liveClassesStatus === 'uninitialized') {
      dispatch(fetchLiveClasses())
    }
  }, [liveClassesStatus])

  useEffect(() => {
    if (liveClassSessionsStatus === 'uninitialized') {
      dispatch(fetchLiveClassSessions())
    }
  }, [liveClassSessionsStatus])

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchLiveClassTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (!formOpen) {
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [formOpen])

  let addLiveClassSession = (value: string) =>
    axios
      .post('/api/v2/admin/live-class/session', {
        liveClassID: selectedLiveClass?.id,
        time: dayjs.utc(value).toDate(),
      })
      .then(() => {
        dispatch(fetchLiveClassSessions())
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  let deleteLiveClassSession = (id: string) =>
    axios
      .delete(`/api/v2/admin/live-class/session?sessionID=${id}`)
      .then(() => {
        dispatch(fetchLiveClassSessions())
      })
      .catch((err) => {
        setErrorMessage(err?.response?.data)
      })

  return (
    <>
      <div className="flex items-center justify-center mt-2 mb-4">
        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
          <LiveTvIcon />
        </Avatar>
        <Typography
          variant="h4"
          className="pl-2"
          style={{
            fontFamily: 'smileAndWave',
          }}
        >
          Live Classes
        </Typography>
      </div>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          <Grid xs={12}>
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              <Button onClick={() => setFormOpen(true)} color="primary">
                Create Live Class
              </Button>
              <Typography sx={{ mb: '2px' }}>
                or search existing live classes to schedule or edit:
              </Typography>
            </Box>
            <Autocomplete
              open={searchOpen}
              onOpen={() => {
                setSearchOpen(true)
              }}
              onClose={() => {
                setSearchOpen(false)
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => `${option.name} | ${option.id}`}
              onInputChange={(_, value) =>
                setSearchedLiveClassID(value.split(' | ')[1])
              }
              options={liveClasses}
              loading={liveClassesStatus === 'pending'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Live Classes"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <SearchIcon sx={{ cursor: 'pointer' }} />
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          </Grid>
          {selectedLiveClass && (
            <>
              <Grid md={6} xs={12}>
                <Card
                  variant="elevation"
                  sx={{
                    height: 575,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <CardMedia
                    sx={{ height: 415 }}
                    image={selectedLiveClass.imageName}
                    title={selectedLiveClass.name}
                  />
                  <CardContent
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <Typography gutterBottom variant="h5" component="div">
                      {selectedLiveClass.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <div>
                        Course Code:{' '}
                        {selectedLiveClass.courseCode || 'No Value Set'}
                      </div>
                      <div>
                        Quiz Passcode:{' '}
                        {selectedLiveClass.quizPasscode || 'No Value Set'}
                      </div>
                    </Typography>
                  </CardContent>
                  <CardActions
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/admin/live/class/${selectedLiveClass?.id}`)
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
              <Grid md={6} xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <StaticDateTimePicker
                    sx={{
                      height: 575,
                      backgroundColor: theme.palette.background.paper,
                    }}
                    disablePast
                    ampm={false}
                    onAccept={(val: any) => addLiveClassSession(val)}
                  />
                </LocalizationProvider>
              </Grid>
              {liveClassSessionsStatus.length > 0 && (
                <Grid xs={6}>
                  <Subheader title={`${selectedLiveClass?.name} Sessions`} />
                  <Grid container spacing={4}>
                    <Timeline
                      // @ts-ignore
                      placeholder={undefined}
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    >
                      {liveClassSessions.map((liveClassSession, i) => (
                        <>
                          <TimelineItem>
                            <TimelineOppositeContent>
                              {dayjs(liveClassSession.time)
                                .utc(true)
                                .local()
                                .format('dddd MMMM D, YYYY HH:mm')}
                            </TimelineOppositeContent>
                            {i < liveClassSessions.length - 1 ? (
                              <TimelineSeparator>
                                <TimelineDot />
                                <TimelineConnector />
                              </TimelineSeparator>
                            ) : (
                              <TimelineSeparator>
                                <TimelineDot />
                              </TimelineSeparator>
                            )}
                            <TimelineContent>
                              <DeleteIcon
                                sx={{ cursor: 'pointer' }}
                                onClick={() =>
                                  deleteLiveClassSession(liveClassSession.id)
                                }
                              />{' '}
                              Delete Session
                            </TimelineContent>
                          </TimelineItem>
                        </>
                      ))}
                    </Timeline>
                  </Grid>
                </Grid>
              )}
              <Grid xs={6}>
                <Subheader title="Full Schedule" />
                <Grid container spacing={4}>
                  <Timeline
                    // @ts-ignore
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  >
                    {allSessions.map((liveClassSession, i) => (
                      <>
                        <TimelineItem>
                          <TimelineOppositeContent>
                            {
                              <a
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                  setSearchedLiveClassID(
                                    liveClassSession.classID,
                                  )
                                }
                              >
                                {
                                  liveClassesGroupedByID?.[
                                    liveClassSession.classID
                                  ]?.[0]?.name
                                }
                              </a>
                            }
                          </TimelineOppositeContent>
                          {i < allSessions.length - 1 ? (
                            <TimelineSeparator>
                              <TimelineDot />
                              <TimelineConnector />
                            </TimelineSeparator>
                          ) : (
                            <TimelineSeparator>
                              <TimelineDot />
                            </TimelineSeparator>
                          )}
                          <TimelineContent>
                            {dayjs(liveClassSession.time)
                              .utc(true)
                              .local()
                              .format('dddd MMMM D, YYYY HH:mm')}
                          </TimelineContent>
                        </TimelineItem>
                      </>
                    ))}
                  </Timeline>
                </Grid>
              </Grid>
            </>
          )}
        </Grid>
      </Container>
      <Dialog open={formOpen} onClose={() => setFormOpen(false)}>
        <form onSubmit={form.handleSubmit}>
          <Paper sx={{ width: 300, px: 2, py: 1 }}>
            <DialogTitle sx={{ textAlign: 'center', marginBottom: -1.5 }}>
              Create Live Class
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 1,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                fullWidth
                variant="standard"
                value={form.values.name}
                label="Name"
                name="name"
                onChange={form.handleChange}
                error={form.touched.name && !!form.errors.name}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.meetingURL}
                label="Meeting URL"
                name="meetingURL"
                onChange={form.handleChange}
                error={form.touched.meetingURL && !!form.errors.meetingURL}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.quizPasscode}
                label="Quiz Passcode"
                name="quizPasscode"
                onChange={form.handleChange}
                error={form.touched.quizPasscode && !!form.errors.quizPasscode}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.code}
                label="Course Code"
                name="code"
                onChange={form.handleChange}
                error={form.touched.code && !!form.errors.code}
              />
              <TextField
                fullWidth
                variant="standard"
                value={form.values.hours}
                label="Hours"
                name="hours"
                onChange={form.handleChange}
                error={form.touched.hours && !!form.errors.hours}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ marginLeft: -2 }}>Template</InputLabel>
                <Select
                  variant="standard"
                  value={form.values.templateID}
                  label="Template"
                  name="templateID"
                  onChange={form.handleChange}
                  error={form.touched.templateID && !!form.errors.templateID}
                >
                  <MenuItem key="unselected" value="unselected">
                    Unselected
                  </MenuItem>
                  {templates.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box>
                <label style={{ marginTop: 0 }}>
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<ImageIcon />}
                  >
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleCapture(e)}
                    />
                    Add Thumbnail
                  </Button>
                </label>
                {fileToUpload && (
                  <Typography variant="subtitle1">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                display: 'flex',
                justifyContent: 'center',
                paddingBottom: 3,
              }}
            >
              {saving ? (
                <CircularProgress size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                    disabled={!fileToUpload}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setFormOpen(false)}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </Paper>
        </form>
      </Dialog>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>Error with request: </strong>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
